import type { List, Map as ImmutableMap } from 'immutable';
import { List as ImmutableListCtor } from 'immutable';

import { isNonStatusId } from 'mastodon/actions/timelines_typed';
import type { Status } from 'mastodon/models/status';

export interface GroupedHomeReplies {
  orderedIds: List<string | null>;
  rootIdFor: Map<string, string>;
}

/**
 * Reorders a Home timeline's status id list so that replies are moved to sit
 * immediately after the post they're replying to (depth-first, siblings
 * oldest-first), instead of appearing as independent top-level entries.
 *
 * A reply is only ever moved when its parent is also present in `statusIds`
 * (Home's `items` list is newest-first, so a reply's parent — being older —
 * normally comes later in the array). Replies whose parent isn't in the
 * currently-loaded window are left exactly where they are, so nothing is
 * ever dropped or hidden.
 *
 * Known tradeoff: a TIMELINE_GAP sentinel's neighboring id may change as a
 * side effect of reordering, which could make its "load more between here"
 * pagination cursor point at a slightly different id than before. Worst
 * case is a redundant/overlapping fetch, which the timelines reducer's
 * existing overlap handling already dedupes safely.
 */
export const groupHomeReplies = (
  statusIds: List<string | null>,
  statuses: ImmutableMap<string, Status>,
): GroupedHomeReplies => {
  const idSet = new Set<string>();

  statusIds.forEach((id) => {
    if (!isNonStatusId(id) && id) idSet.add(id);
  });

  const childrenOf = new Map<string, string[]>();
  const parentOf = new Map<string, string>();

  statusIds.forEach((id) => {
    if (isNonStatusId(id) || !id) return;

    const status = statuses.get(id);
    if (!status || status.get('reblog')) return;

    const replyToId = status.get('in_reply_to_id') as string | null;
    if (!replyToId || !idSet.has(replyToId)) return;

    const parentStatus = statuses.get(replyToId);
    if (!parentStatus || parentStatus.get('reblog')) return;

    parentOf.set(id, replyToId);
    const siblings = childrenOf.get(replyToId);
    if (siblings) {
      siblings.push(id);
    } else {
      childrenOf.set(replyToId, [id]);
    }
  });

  const rootIdFor = new Map<string, string>();
  const orderedIds: (string | null)[] = [];

  const emitSubtree = (id: string, rootId: string) => {
    orderedIds.push(id);
    rootIdFor.set(id, rootId);

    const children = childrenOf.get(id);
    if (!children) return;

    // Children were collected in newest-first scan order; reverse so
    // siblings display oldest-first, the natural comment-thread reading
    // order (matching selectors/contexts.ts's getDescendantsIds).
    for (let i = children.length - 1; i >= 0; i--) {
      const childId = children[i];
      if (childId) emitSubtree(childId, rootId);
    }
  };

  statusIds.forEach((id) => {
    if (isNonStatusId(id) || !id) {
      orderedIds.push(id); // sentinel passthrough (e.g. TIMELINE_GAP is null)
      return;
    }

    if (parentOf.has(id)) return; // will be emitted under its parent instead

    emitSubtree(id, id);
  });

  return { orderedIds: ImmutableListCtor(orderedIds), rootIdFor };
};

export const isNestedReply = (
  rootIdFor: Map<string, string>,
  id: string | null,
): boolean => {
  if (!id) return false;
  const rootId = rootIdFor.get(id);
  return rootId !== undefined && rootId !== id;
};
